/**
 * STUNDENKONTINGENT DER PAKETPOSITION — reine Logik, kein Schreibpfad.
 *
 * Eine Paketposition („1 psch Planungsphase") ist intern mit einem
 * Stundenkontingent kalkuliert („= 100 h"). Wer einen Bestandteil erledigt,
 * gibt die Stunden dazu an; die Summe wird gegen das Kontingent gestellt —
 * darunter gruen, darueber rot. Auftrag, Entscheide S1–S10 und Schnittstellen:
 * docs/stundenkontingent-vertrag.md.
 *
 * KERNREGELN, die dieses Modul abbildet:
 *
 *  1. Rein intern (S8). Stunden und Kontingent sperren die Abrechnung NIE und
 *     stehen nie auf Rechnung, Leistungsnachweis, E-Rechnung, Angebots-PDF.
 *     Nichts hier fasst eine Menge oder einen Preis an.
 *  2. Summe (S4): alle AKTIVEN Eintraege der Position zaehlen — erledigt UND
 *     entfaellt, auch zu inzwischen entfernten Keys (die Arbeit ist
 *     geleistet). Zurueckgenommene zaehlen nicht.
 *  3. Ampel (S5): erfasst ≤ Kontingent → gruen (auch genau gleich), darueber
 *     rot. Ohne Kontingent neutral. Verglichen wird in ganzen Hundertstel-
 *     stunden — eine Summe aus Kommazahlen darf nie wegen eines Float-Rests
 *     („100.00000000000001") rot werden.
 *  4. Zwei Quellen, nie vermischt: `stundenStand` rechnet aus den Eintraegen
 *     (Dialog, kennt auch „ohne Stunden"), `stundenStandAusPosition` nimmt die
 *     DB-gepflegte Summe `stunden_erfasst` (Hub-Plakette, ohne Eintraege). Die
 *     beiden koennen fuer einen Moment auseinanderliegen (frisch gebucht,
 *     Position noch nicht neu geladen).
 *
 * ABHAENGIGKEIT: nur bestandteilLogic (Werte) und Typen. `formatStunden` und
 * `stundenAusDb` wohnen in bestandteilLogic und werden hier nur
 * weitergereicht — der Verlauf dort braucht sie, und bestandteilLogic bleibt
 * so Blattmodul (ein Wert-Import in Gegenrichtung baute einen Zyklus).
 *
 * Pure Funktionen, kein Supabase, kein React, kein Netz.
 */

import type { ServiceItem } from '../../types';
import {
    bestandteilStand,
    formatStunden,
    formatStundenZahl,
    hatBestandteile,
    stundenAusDb,
    type BestandteilEintrag,
} from './bestandteilLogic';

export { formatStunden, formatStundenZahl, stundenAusDb };

// ─────────────────────────────────────────────────────────────────────────────
// Grenzen und Eingabe (S9)
// ─────────────────────────────────────────────────────────────────────────────

/** Hoechstwert je Eintrag — wie der CHECK auf `position_bestandteil_eintraege.stunden` (Nr. 171). */
export const STUNDEN_MAX = 9999;
/** Hoechstwert des Kontingents — wie der CHECK auf `project_services.stunden_kontingent` (Nr. 171). */
export const KONTINGENT_MAX = 99999;

/**
 * Ganze Zahl, wahlweise mit Komma ODER Punkt und ein bis zwei Nachkommastellen;
 * auch „,5". Kein Vorzeichen, kein Exponent, kein Tausendertrenner — „1.000"
 * hat damit drei Nachkommastellen und wird ABGELEHNT, nie als 1 gelesen.
 */
const STUNDEN_MUSTER = /^(\d*)(?:[.,](\d{1,2}))?$/;

/**
 * '7,5' | '7.5' | ' 8 ' → 7.5. Leer, keine Zahl, mehr als 2 Nachkommastellen,
 * ≤ 0 oder > max → null. `nullStundenErlaubt`: 0 h ist zulaessig (entfaellt).
 * Vorgabe max = STUNDEN_MAX.
 *
 * Ein angehaengtes „h" ist erlaubt („7,5 h"), damit ein mit formatStunden
 * vorbelegtes Feld (bis 9999 h) ohne Nacharbeit wieder lesbar ist.
 *
 * Bewusst streng: was hier durchgeht, prueft die Datenbank noch einmal
 * (`stunden_ungueltig`) — eine Eingabe, die erst am Server scheitert, waere
 * fuer den Nutzer nur ein Umweg.
 */
export const parseStunden = (eingabe: string, opts: { max?: number; nullStundenErlaubt?: boolean } = {}): number | null => {
    const roh = String(eingabe ?? '')
        .trim()
        .replace(/\s*h$/i, '');
    if (!roh) return null;
    const m = STUNDEN_MUSTER.exec(roh);
    if (!m || (!m[1] && !m[2])) return null;
    const wert = Number(`${m[1] || '0'}.${m[2] || '0'}`);
    if (!Number.isFinite(wert)) return null;
    const max = typeof opts?.max === 'number' && Number.isFinite(opts.max) && opts.max > 0 ? opts.max : STUNDEN_MAX;
    if (wert > max) return null;
    if (wert === 0) return opts?.nullStundenErlaubt ? 0 : null;
    return wert;
};

// ─────────────────────────────────────────────────────────────────────────────
// Stand einer Position
// ─────────────────────────────────────────────────────────────────────────────

export type StundenAmpel = 'ohne_kontingent' | 'gruen' | 'rot';

export interface StundenStand {
    kontingent: number | null;
    erfasst: number;
    /** kontingent − erfasst (negativ = über); null ohne Kontingent. */
    rest: number | null;
    /** max(0, erfasst − kontingent); 0 ohne Kontingent. */
    ueber: number;
    ampel: StundenAmpel;
    /** Aktive Einträge mit art 'erledigt' zu Keys der Definition, ohne Stunden. */
    ohneStunden: number;
    /** erfasst / kontingent, für den Balken (nicht gedeckelt); null ohne Kontingent. */
    anteil: number | null;
}

/** In ganzen Hundertstelstunden — alle Summen und Vergleiche laufen darueber (Kernregel 3). */
const cent = (h: number): number => Math.round(h * 100);

/**
 * Das Kontingent als Zahl > 0, sonst null. 0 und negative Werte verbietet der
 * CHECK (Nr. 171); stehen sie trotzdem da (Handeingriff), gilt die Position
 * als „ohne Kontingent" — nie als „alles ueber dem Kontingent", und nie eine
 * Division durch 0 im Balken.
 */
const kontingentWert = (raw: unknown): number | null => {
    const k = stundenAusDb(raw);
    return k !== null && k > 0 ? k : null;
};

/** Stunden eines Eintrags oder einer Summe: Zahl ≥ 0, sonst null (fehlt/kaputt). */
const stundenWert = (raw: unknown): number | null => {
    const h = stundenAusDb(raw);
    return h !== null && h >= 0 ? h : null;
};

const standAus = (kontingent: number | null, erfasstCent: number, ohneStunden: number): StundenStand => {
    const erfasst = Math.max(0, erfasstCent) / 100;
    if (kontingent === null) {
        return { kontingent: null, erfasst, rest: null, ueber: 0, ampel: 'ohne_kontingent', ohneStunden, anteil: null };
    }
    const differenz = cent(kontingent) - Math.max(0, erfasstCent);
    return {
        kontingent,
        erfasst,
        rest: differenz / 100,
        ueber: differenz < 0 ? -differenz / 100 : 0,
        ampel: differenz < 0 ? 'rot' : 'gruen',
        ohneStunden,
        anteil: erfasst / kontingent,
    };
};

/**
 * Aus den Einträgen (Dialog) — Summe aller aktiven Einträge der Position (S4).
 *
 * `ohneStunden` zaehlt dagegen nur die ZEILEN des Dialogs: je Key der
 * Definition der wirksame aktive Eintrag (bestandteilStand — derselbe, an dem
 * der Knopf „Stunden eintragen" haengt), und davon nur die erledigten.
 * „Entfaellt" braucht keine Stunden (S2), ein verwaister Eintrag hat keine
 * Zeile, an der man sie nachtragen koennte.
 */
export const stundenStand = (
    service: Pick<ServiceItem, 'id' | 'bestandteile' | 'stundenKontingent'>,
    eintraege: readonly BestandteilEintrag[],
): StundenStand => {
    const serviceId = String(service?.id ?? '');
    let summeCent = 0;
    for (const e of eintraege || []) {
        if (!e || String(e.serviceId) !== serviceId || e.zurueckgenommenAm) continue;
        const h = stundenWert(e.stunden);
        if (h !== null) summeCent += cent(h);
    }
    const ohneStunden = bestandteilStand(service, eintraege || []).zeilen.filter(
        (z) => z.status === 'erledigt' && stundenWert(z.eintrag?.stunden) === null,
    ).length;
    return standAus(kontingentWert(service?.stundenKontingent), summeCent, ohneStunden);
};

/**
 * Aus den DB-Feldern der Position (Hub-Plakette). null: kein Paket, oder weder
 * Kontingent noch erfasste Stunden bekannt. ohneStunden = 0 (unbekannt).
 *
 * Ist nur das Kontingent bekannt, zaehlt „erfasst" als 0: `stunden_erfasst`
 * rechnet der Trigger aus Nr. 172 bei JEDEM Schreiben der Position neu, auch
 * beim Setzen des Kontingents — ein Kontingent ohne Summe kommt nur vor, wenn
 * die Spalte nicht gelesen wurde.
 */
export const stundenStandAusPosition = (
    service: Pick<ServiceItem, 'bestandteile' | 'stundenKontingent' | 'stundenErfasst'>,
): StundenStand | null => {
    if (!hatBestandteile(service)) return null;
    const kontingent = kontingentWert(service?.stundenKontingent);
    const erfasst = stundenWert(service?.stundenErfasst);
    if (kontingent === null && erfasst === null) return null;
    return standAus(kontingent, erfasst === null ? 0 : cent(erfasst), 0);
};

// ─────────────────────────────────────────────────────────────────────────────
// Anzeige
// ─────────────────────────────────────────────────────────────────────────────

const istOhneKontingent = (s: StundenStand): boolean => s.ampel === 'ohne_kontingent' || s.kontingent === null;

/**
 * 'gruen': „62,5 von 100 h · 37,5 h frei"; 'rot': „112 von 100 h · 12 h über
 * dem Kontingent"; ohne: „62,5 h erfasst · kein Kontingent hinterlegt".
 */
export const stundenText = (s: StundenStand): string => {
    if (!s) return '';
    if (istOhneKontingent(s)) return `${formatStunden(s.erfasst)} erfasst · kein Kontingent hinterlegt`;
    const kopf = `${formatStundenZahl(s.erfasst)} von ${formatStunden(s.kontingent as number)}`;
    if (s.ampel === 'rot') return `${kopf} · ${formatStunden(s.ueber)} über dem Kontingent`;
    return `${kopf} · ${formatStunden(Math.max(0, s.rest ?? 0))} frei`;
};

/** Kurzform für die Plakette: „62,5/100 h" bzw. „62,5 h"; ton gruen/rot/grau. */
export const stundenPlakette = (s: StundenStand): { text: string; ton: 'gruen' | 'rot' | 'grau' } => {
    if (!s) return { text: '', ton: 'grau' };
    if (istOhneKontingent(s)) return { text: formatStunden(s.erfasst), ton: 'grau' };
    return {
        text: `${formatStundenZahl(s.erfasst)}/${formatStunden(s.kontingent as number)}`,
        ton: s.ampel === 'rot' ? 'rot' : 'gruen',
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Protokoll (`position_stunden_protokoll`, Nr. 171)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Eine Aenderung von Stunden: `art 'eintrag'` = Stunden eines Bestandteil-
 * Eintrags nachgetragen/geaendert (`bestandteil_stunden_setzen`),
 * `art 'kontingent'` = Kontingent der Position gesetzt/geaendert/entfernt
 * (`position_stundenkontingent_setzen`). Die Ersterfassung beim Erledigen
 * steht im Eintrag selbst, nicht hier.
 */
export interface StundenProtokollEintrag {
    id: string;
    projectId: string;
    serviceId: string;
    eintragId: string | null;
    art: 'eintrag' | 'kontingent';
    vorher: number | null;
    nachher: number | null;
    grund: string | null;
    geaendertAm: string;
    geaendertVon: string | null;
    geaendertVonName: string | null;
}

/** Nicht-leerer Text oder null. Zahlen (z. B. eine integer-id) werden zu Text. */
const text = (v: unknown): string | null => {
    if (typeof v === 'string') {
        const t = v.trim();
        return t ? t : null;
    }
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return null;
};

/**
 * Zeile aus der DB (snake_case) → StundenProtokollEintrag; null bei
 * unbrauchbarer Zeile (Pflichtfeld fehlt, unbekannte `art`). Nie Wurf — eine
 * kaputte Zeile darf den Verlauf nicht leeren, sie faellt nur heraus.
 */
export const stundenProtokollAusZeile = (row: Record<string, unknown>): StundenProtokollEintrag | null => {
    if (!row || typeof row !== 'object') return null;
    const id = text(row.id);
    const projectId = text(row.project_id);
    const serviceId = text(row.service_id);
    const geaendertAm = text(row.geaendert_am);
    const art = row.art;
    if (!id || !projectId || !serviceId || !geaendertAm) return null;
    if (art !== 'eintrag' && art !== 'kontingent') return null;
    return {
        id,
        projectId,
        serviceId,
        eintragId: text(row.eintrag_id),
        art,
        vorher: stundenAusDb(row.vorher),
        nachher: stundenAusDb(row.nachher),
        grund: text(row.grund),
        geaendertAm,
        geaendertVon: text(row.geaendert_von),
        geaendertVonName: text(row.geaendert_von_name),
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Rechte (S7) — Spiegel von `bestandteil_stunden_setzen` (Nr. 172)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * S7 — welche Stunden-Handlungen die Person an einem Eintrag hat.
 *
 *  - `schreibbar` false (Freelancer, Nur-Ansicht, kein Projektzugriff) oder
 *    Eintrag zurueckgenommen → nichts.
 *  - Stunden fehlen → `nachtragen` fuer jeden Berechtigten.
 *  - Stunden stehen → `aendern` nur, wer den Eintrag erfasst hat, oder ein
 *    Admin (mit Grund — den verlangt der Dialog, die DB prueft ihn).
 *
 * Nur fuer die Anzeige der Knoepfe; die Entscheidung trifft die Datenbank.
 * Ohne angemeldete Person (`userId` null) ist kein Eintrag „der eigene".
 */
export const stundenRechte = (a: {
    eintrag: Pick<BestandteilEintrag, 'stunden' | 'erfasstVon' | 'zurueckgenommenAm'>;
    userId: string | null;
    isAdmin: boolean;
    schreibbar: boolean;
}): { nachtragen: boolean; aendern: boolean } => {
    const nichts = { nachtragen: false, aendern: false };
    const e = a?.eintrag;
    if (!e || !a.schreibbar || e.zurueckgenommenAm) return nichts;
    if (stundenWert(e.stunden) === null) return { nachtragen: true, aendern: false };
    // uuid-Vergleich ohne Gross-/Kleinschreibung (wie zeilenKnoepfe) — die
    // Sitzung und PostgREST liefern dieselbe Kennung nicht immer gleich geschrieben.
    const eigener = !!a.userId && !!e.erfasstVon
        && String(e.erfasstVon).trim().toLowerCase() === String(a.userId).trim().toLowerCase();
    return { nachtragen: false, aendern: a.isAdmin === true || eigener };
};
